import React, { useCallback, useEffect, useMemo, useState } from 'react'
import { useClerk, useSession, useUser, useAuth as useClerkAuth } from '@clerk/clerk-react'
import { useAuthStore } from '@/stores/authStore'
import type { AuthContextType, OrgTier, Organization, Permission, Session, User, UserRole } from '../types'
import { rolePermissions } from '../types'
import { ensureOrgMembership } from '@/data/bootstrap/ensureOrgMembership'
import { queryClient } from '@/data/QueryProvider'

function clerkComponentWarning(name: string): void {
  console.warn(`[Clerk] ${name}() called directly — use the <SignIn> / <SignUp> component instead.`)
}

function coerceName(user: ReturnType<typeof useUser>['user']): string {
  if (!user) return ''
  const email =
    user.primaryEmailAddress?.emailAddress ||
    user.emailAddresses?.[0]?.emailAddress ||
    ''
  return user.fullName || user.firstName || email.split('@')[0] || 'User'
}

export function ClerkAuthProvider({ children }: { children: React.ReactNode }) {
  const clerk = useClerk()
  const { isLoaded: isAuthLoaded, isSignedIn } = useClerkAuth()
  const { user: clerkUser, isLoaded: isUserLoaded } = useUser()
  const { session: clerkSession } = useSession()

  const [orgFromDb, setOrgFromDb] = useState<Organization | null>(null)
  const [isBootstrapping, setIsBootstrapping] = useState(false)
  const [bootstrapError, setBootstrapError] = useState<string | null>(null)

  // Optional local overrides (demo/testing convenience).
  const [overrideRole, setOverrideRole] = useState<UserRole | null>(null)
  const [overrideTier, setOverrideTier] = useState<OrgTier | null>(null)

  // Derive session from Clerk's session data.
  const session: Session | null = useMemo(() => {
    if (!clerkSession) return null
    const createdAt = clerkSession.createdAt ? new Date(clerkSession.createdAt).getTime() : Date.now()
    const expiresAt = clerkSession.expireAt ? new Date(clerkSession.expireAt).getTime() : createdAt + 30 * 24 * 60 * 60 * 1000
    return { createdAt, expiresAt }
  }, [clerkSession])

  const clerkUserId = clerkUser?.id
  const user: User | null = useMemo(() => {
    if (!isSignedIn || !clerkUser) return null
    const email =
      clerkUser.primaryEmailAddress?.emailAddress ||
      clerkUser.emailAddresses?.[0]?.emailAddress ||
      ''
    return {
      id: clerkUser.id,
      email,
      name: coerceName(clerkUser),
      avatarUrl: clerkUser.imageUrl || undefined,
    }
  }, [isSignedIn, clerkUserId])

  useEffect(() => {
    let cancelled = false

    async function run() {
      if (!isSignedIn || !clerkUser) return
      setIsBootstrapping(true)
      setBootstrapError(null)
      try {
        const org = await ensureOrgMembership({
          clerkId: clerkUser.id,
          email:
            clerkUser.primaryEmailAddress?.emailAddress ||
            clerkUser.emailAddresses?.[0]?.emailAddress ||
            '',
          name: coerceName(clerkUser),
          avatarUrl: clerkUser.imageUrl || undefined,
        })
        if (!cancelled) {
          setOrgFromDb(org)
        }
      } catch (err) {
        if (!cancelled) {
          setBootstrapError(err instanceof Error ? err.message : 'Failed to bootstrap user/org')
          setOrgFromDb(null)
        }
      } finally {
        if (!cancelled) setIsBootstrapping(false)
      }
    }

    // Reset when signed out.
    if (isAuthLoaded && isUserLoaded && !isSignedIn) {
      setOrgFromDb(null)
      setBootstrapError(null)
      setIsBootstrapping(false)
      return
    }

    if (isAuthLoaded && isUserLoaded && isSignedIn) {
      void run()
    }

    return () => {
      cancelled = true
    }
  // Depend on clerkUserId (stable string) instead of clerkUser (new object each render).
  }, [isAuthLoaded, isSignedIn, isUserLoaded, clerkUserId])

  const organization: Organization | null = useMemo(() => {
    if (!isSignedIn) return null
    if (!orgFromDb) return null
    // Platform admins get the super_admin role server-side (via platform_admins),
    // which is the real source of truth now that Dev Tools no longer ships to prod.
    const isPlatformAdmin = !!orgFromDb.isPlatformAdmin
    const baseRole: UserRole = isPlatformAdmin ? 'super_admin' : orgFromDb.role
    // The local role override is a demo/testing convenience and must never be able
    // to fabricate super_admin: that authority comes only from the DB. (The DB RPCs
    // enforce is_platform_admin() regardless, so this only closes the UI gate.)
    const requestedRole = overrideRole ?? baseRole
    const role: UserRole =
      requestedRole === 'super_admin' && !isPlatformAdmin ? orgFromDb.role : requestedRole
    return {
      ...orgFromDb,
      role,
      tier: overrideTier ?? orgFromDb.tier,
    }
  }, [isSignedIn, orgFromDb, overrideRole, overrideTier])

  const signOut = useCallback(async () => {
    queryClient.clear()
    await clerk.signOut({ redirectUrl: '/' })
  }, [clerk])

  const updateUserName = useCallback(async (name: string) => {
    if (!clerkUser) return
    const trimmed = name.trim()
    const parts = trimmed.split(/\s+/).filter(Boolean)
    const firstName = parts[0] || trimmed
    const lastName = parts.length > 1 ? parts.slice(1).join(' ') : ''
    await clerkUser.update({ firstName, lastName })
  }, [clerkUser])

  const updateUserAvatar = useCallback(async (file: File | null) => {
    if (!clerkUser) throw new Error('Not signed in')
    await clerkUser.setProfileImage({ file })
    // clerkUser.imageUrl updates reactively via Clerk's User object;
    // the user memo re-derives avatarUrl on next render.
  }, [clerkUser])

  const setUserRole = useCallback((role: UserRole) => {
    setOverrideRole(role)
  }, [])

  const setOrgTier = useCallback((tier: OrgTier) => {
    setOverrideTier(tier)
  }, [])

  // Both downgrade actions defer to Stripe Customer Portal: cancel/reactivate the subscription
  // there. Webhook events sync the resulting state back to Postgres.
  const requestDowngrade = useCallback(async (_toTier: OrgTier, _gracePeriodDays?: number) => {
    if (!orgFromDb?.id) throw new Error('No organization loaded')
    const { createPortalSession } = await import('@/lib/stripeClient')
    await createPortalSession(orgFromDb.id)
  }, [orgFromDb])

  const cancelDowngrade = useCallback(async () => {
    if (!orgFromDb?.id) throw new Error('No organization loaded')
    const { createPortalSession } = await import('@/lib/stripeClient')
    await createPortalSession(orgFromDb.id)
  }, [orgFromDb])

  const hasPermission = useCallback((permission: Permission) => {
    if (!organization) return false
    return rolePermissions[organization.role].includes(permission)
  }, [organization])

  const hasRole = useCallback((roles: UserRole | UserRole[]) => {
    if (!organization) return false
    const wanted = Array.isArray(roles) ? roles : [roles]
    return wanted.includes(organization.role)
  }, [organization])

  const simulateExpiredSession = useCallback(() => {
    // No-op in Clerk mode; Clerk manages session lifecycle.
  }, [])

  // `isLoaded` means "auth state is settled enough to make permission
  // decisions." For signed-in users that requires the database organization
  // row to have hydrated (or the bootstrap to have explicitly failed),
  // otherwise PermissionRoute would briefly see organization=null between
  // Clerk loading and the bootstrap effect firing — long enough to render
  // a <Navigate to="/forbidden"> on direct navigation or reload.
  const value: AuthContextType = {
    isLoaded:
      isAuthLoaded &&
      isUserLoaded &&
      !isBootstrapping &&
      (!isSignedIn || orgFromDb !== null || bootstrapError !== null),
    isSignedIn: !!isSignedIn,
    user,
    organization,
    session,
    // Auth widgets are rendered via Clerk's <SignIn>/<SignUp> components.
    // These methods exist to satisfy AuthContextType for demo-mode parity.
    signIn: async () => clerkComponentWarning('signIn'),
    signInWithGoogle: async () => clerkComponentWarning('signInWithGoogle'),
    signInWithGithub: async () => clerkComponentWarning('signInWithGithub'),
    signInWithMagicLink: async () => clerkComponentWarning('signInWithMagicLink'),
    signUp: async () => clerkComponentWarning('signUp'),
    signOut,
    updateUserName,
    updateUserAvatar,
    setUserRole,
    setOrgTier,
    requestDowngrade,
    cancelDowngrade,
    hasPermission,
    hasRole,
    simulateExpiredSession,
  }

  // Sync to Zustand store (replaces Context.Provider).
  // useEffect avoids publishing state from discarded renders in StrictMode/concurrent mode.
  React.useEffect(() => {
    useAuthStore.setState(value)
  }, [value])

  if (bootstrapError) {
    return (
      <div className="min-h-screen flex items-center justify-center p-6">
        <div className="max-w-lg w-full bg-card text-card-foreground border rounded-xl p-6">
          <h1 className="text-lg font-semibold">Account Setup Error</h1>
          <p className="mt-2 text-sm text-muted-foreground">
            We couldn&apos;t finish setting up your account for this environment.
          </p>
          <pre className="mt-4 text-xs overflow-auto rounded-lg bg-muted p-3 whitespace-pre-wrap">{bootstrapError}</pre>
        </div>
      </div>
    )
  }

  return <>{children}</>
}
